import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useApp } from '@/components/AppLayout';
import {
  reportsApi,
  type SalesByClientReport,
  type ClientSalesRow,
  type ReportExportFormat,
} from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import {
  ReportLayout,
  SummaryCard,
  SummaryCardsGrid,
  ExportDropdown,
  TimeRangePicker,
  createTimeRange,
  type TimeRange,
} from '@/components/reports';
import type { ColumnDef } from '@tanstack/react-table';
import { Users } from 'lucide-react';

export default function SalesByClient() {
  const { currentBusiness } = useApp();
  const [report, setReport] = useState<SalesByClientReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>(() => createTimeRange('this_year'));

  useEffect(() => {
    if (currentBusiness) {
      loadReport();
    }
  }, [currentBusiness, timeRange]);

  const loadReport = async () => {
    if (!currentBusiness) return;
    setLoading(true);
    try {
      const response = await reportsApi.getSalesByClient(
        currentBusiness.id,
        timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
        timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
        timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined
      );
      if (response.data) {
        setReport(response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportFormat: ReportExportFormat) => {
    if (!currentBusiness) return;
    await reportsApi.exportSalesByClient(
      currentBusiness.id,
      exportFormat,
      timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
      timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
      timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined
    );
  };

  const columns: ColumnDef<ClientSalesRow>[] = [
    {
      accessorKey: 'rank',
      header: '#',
      cell: ({ row }) => (
        <div className="font-medium text-muted-foreground">{row.getValue('rank')}</div>
      ),
    },
    {
      accessorKey: 'client_name',
      header: ({ column }) => <SortableHeader column={column} title="Client" />,
    },
    {
      accessorKey: 'invoice_count',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Invoices" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">{row.getValue('invoice_count')}</div>
      ),
    },
    {
      accessorKey: 'revenue',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Revenue" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right font-medium">
          {formatAmount(row.getValue('revenue'))}
        </div>
      ),
    },
    {
      accessorKey: 'amount_paid',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Paid" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right text-green-600">
          {formatAmount(row.getValue('amount_paid'))}
        </div>
      ),
    },
    {
      accessorKey: 'outstanding',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Outstanding" />
        </div>
      ),
      cell: ({ row }) => {
        const value = row.getValue('outstanding') as string;
        const isNonZero = parseFloat(value) > 0;
        return (
          <div className={`text-right ${isNonZero ? 'text-amber-600' : 'text-muted-foreground'}`}>
            {formatAmount(value)}
          </div>
        );
      },
    },
    {
      accessorKey: 'percent_of_total',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="% of Total" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right text-muted-foreground">
          {row.getValue('percent_of_total')}%
        </div>
      ),
    },
  ];

  return (
    <ReportLayout
      title="Sales by Client"
      actions={<ExportDropdown onExport={handleExport} disabled={loading || !report} />}
      filters={
        <TimeRangePicker
          value={timeRange}
          onChange={setTimeRange}
        />
      }
    >
      {report && (
        <div className="mb-6">
          <SummaryCardsGrid>
            <SummaryCard
              label="Total Revenue"
              value={formatAmount(report.summary.total_revenue)}
              variant="success"
            />
            <SummaryCard
              label="Active Clients"
              value={report.summary.active_clients.toString()}
            />
            <SummaryCard
              label="Average per Client"
              value={formatAmount(report.summary.avg_per_client)}
            />
            <SummaryCard
              label="Outstanding"
              value={formatAmount(report.summary.total_outstanding)}
              variant={parseFloat(report.summary.total_outstanding) > 0 ? 'warning' : 'default'}
            />
          </SummaryCardsGrid>
        </div>
      )}
      <DataTable
        columns={columns}
        data={report?.data ?? []}
        loading={loading}
        emptyIcon={<Users className="h-6 w-6 text-muted-foreground" />}
        emptyTitle="No Client Sales"
        emptyDescription="No client sales data found in this period."
      />
    </ReportLayout>
  );
}
